import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../../../api";
import { useNavigate } from 'react-router-dom';

// Component for managing data retention, backup, and security settings
const DataManagement = () => {
  // State variables for data retention and backup settings
  const [dataRetentionTenure, setDataRetentionTenure] = useState("");
  const [backupSchedule, setBackupSchedule] = useState("");
  const [localStorageEnabled, setLocalStorageEnabled] = useState(false);
  const [localStoragePath, setLocalStoragePath] = useState("");
  const [cloudStorageEnabled, setCloudStorageEnabled] = useState(false);
  const [cloudStoragePath, setCloudStoragePath] = useState("");
  // State variables for password policy settings
  const [minEightChar, setMinEightChar] = useState(false);
  const [atLeastOneSymbol, setAtLeastOneSymbol] = useState(false);
  const [atLeastOneNumber, setAtLeastOneNumber] = useState(false);
  const [includeLowerUppercase, setIncludeLowerUppercase] = useState(false);
  // State variable for session timeout
  const [inactivityTimeout, setInactivityTimeout] = useState("");
  // State variables for loading states
  const [loadingRetention, setLoadingRetention] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  // Hook for programmatic navigation
  const navigate = useNavigate();

  // 🕒 Effect for handling session timeout based on user inactivity
  useEffect(() => {
    let timeoutId;

    // Function to log out user and clear session
    const handleLogout = () => {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "https://ros.topdevit.com/"; // ✅ Redirect to login page
    };

    // Function to reset the inactivity timeout
    const resetTimeout = () => {
      if (inactivityTimeout && inactivityTimeout !== "never") {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          handleLogout();
        }, parseInt(inactivityTimeout) * 60 * 1000); // Convert minutes to ms
      }
    };

    // Handler for user activity to reset timeout
    const handleUserActivity = () => {
      resetTimeout();
    };

    // Add event listeners for user activity
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    // Initial timeout setup
    resetTimeout();

    // Cleanup event listeners and timeout on unmount
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, [inactivityTimeout]);

  // Effect to fetch data management settings on component mount
  useEffect(() => {
    fetchDataManagementSettings();
  }, []);

  // Function to fetch data management settings from the server
  const fetchDataManagementSettings = async () => {
    try {
      setLoadingData(true);
      const response = await axiosInstance.get("/ems/get-data-management-security");

      if (response.data.status === "success") {
        const data = response.data.data;

        // Set form values from API response
        setDataRetentionTenure(data.data_retention_tenure?.toString() || "");
        setBackupSchedule(data.backup_schedule || "");
        setLocalStorageEnabled(data.local_storage === 1);
        setLocalStoragePath(data.local_storage_path || "");
        setCloudStorageEnabled(data.cloud_storage === 1);
        setCloudStoragePath(data.cloud_storage_path || "");
        setMinEightChar(data.is_min_eight_char === 1);
        setAtLeastOneSymbol(data.is_atleast_one_symbol === 1);
        setAtLeastOneNumber(data.is_atleast_one_number === 1);
        setIncludeLowerUppercase(data.is_include_lower_and_uppercase === 1);
        setInactivityTimeout(data.inactivity_timeout?.toString() || "");

        // Save password policies to sessionStorage
        sessionStorage.setItem('password_min_length', data.is_min_eight_char === 1 ? '8' : '0');
        sessionStorage.setItem('password_require_symbol', data.is_atleast_one_symbol === 1 ? 'true' : 'false');
        sessionStorage.setItem('password_require_number', data.is_atleast_one_number === 1 ? 'true' : 'false');
        sessionStorage.setItem('password_require_upper_lower', data.is_include_lower_and_uppercase === 1 ? 'true' : 'false');
      }
    } catch (error) {
      console.error("Error fetching data management settings:", error.response?.data || error.message);
      alert("Error while fetching data management settings: " + (error.response?.data?.message || error.message));
    } finally {
      setLoadingData(false);
    }
  };

  // Function to save data retention and backup settings
  const handleSaveRetention = async () => {
    if (!dataRetentionTenure || !backupSchedule) {
      alert("Please select data retention tenure and backup schedule.");
      return;
    }
    if (localStorageEnabled && !localStoragePath) {
      alert("Please provide a local storage path.");
      return;
    }
    if (cloudStorageEnabled && !cloudStoragePath) {
      alert("Please provide a cloud storage path.");
      return;
    }
    setLoadingRetention(true);
    try {
      const payload = {
        data_retention_tenure: dataRetentionTenure,
        backup_schedule: backupSchedule,
        local_storage: localStorageEnabled ? "1" : "0",
        local_storage_path: localStoragePath,
        cloud_storage: cloudStorageEnabled ? "1" : "0",
        cloud_storage_path: cloudStoragePath,
        is_min_eight_char: minEightChar ? "1" : "0",
        is_atleast_one_symbol: atLeastOneSymbol ? "1" : "0",
        is_atleast_one_number: atLeastOneNumber ? "1" : "0",
        is_include_lower_and_uppercase: includeLowerUppercase ? "1" : "0",
        inactivity_timeout: inactivityTimeout || "30",
      };

      const response = await axiosInstance.post("/ems/save-data-management-security", payload);

      if (response.data.status === "success") {
        alert("Data retention settings updated successfully!");
        sessionStorage.setItem('password_min_length', minEightChar ? '8' : '0');
        sessionStorage.setItem('password_require_symbol', atLeastOneSymbol ? 'true' : 'false');
        sessionStorage.setItem('password_require_number', atLeastOneNumber ? 'true' : 'false');
        sessionStorage.setItem('password_require_upper_lower', includeLowerUppercase ? 'true' : 'false');
      } else {
        alert("Failed to save data retention settings.");
      }
    } catch (error) {
      console.error("Error saving data retention settings:", error.response?.data || error.message);
      alert("Error while saving data retention settings: " + (error.response?.data?.message || error.message));
    } finally {
      setLoadingRetention(false);
    }
  };

  // Function to save password policy settings
  const handleSavePasswordPolicies = async () => {
    if (!minEightChar && !atLeastOneSymbol && !atLeastOneNumber && !includeLowerUppercase) {
      alert("Please enable at least one password policy.");
      return;
    }
    setLoadingPassword(true);
    try {
      const payload = {
        data_retention_tenure: dataRetentionTenure || "12",
        backup_schedule: backupSchedule || "Monthly",
        local_storage: localStorageEnabled ? "1" : "0",
        local_storage_path: localStoragePath,
        cloud_storage: cloudStorageEnabled ? "1" : "0",
        cloud_storage_path: cloudStoragePath,
        is_min_eight_char: minEightChar ? "1" : "0",
        is_atleast_one_symbol: atLeastOneSymbol ? "1" : "0",
        is_atleast_one_number: atLeastOneNumber ? "1" : "0",
        is_include_lower_and_uppercase: includeLowerUppercase ? "1" : "0",
        inactivity_timeout: inactivityTimeout || "30",
      };

      const response = await axiosInstance.post("/ems/save-data-management-security", payload);

      if (response.data.status === "success") {
        alert("Password policies updated successfully!");
        sessionStorage.setItem('password_min_length', minEightChar ? '8' : '0');
        sessionStorage.setItem('password_require_symbol', atLeastOneSymbol ? 'true' : 'false');
        sessionStorage.setItem('password_require_number', atLeastOneNumber ? 'true' : 'false');
        sessionStorage.setItem('password_require_upper_lower', includeLowerUppercase ? 'true' : 'false');
      } else {
        alert("Failed to save password policies.");
      }
    } catch (error) {
      console.error("Error saving password policies:", error.response?.data || error.message);
      alert("Error while saving password policies: " + (error.response?.data?.message || error.message));
    } finally {
      setLoadingPassword(false);
    }
  };

  // Function to save session timeout settings
  const handleSaveTimeout = async () => {
    if (!inactivityTimeout) {
      alert("Please select an inactivity timeout duration.");
      return;
    }
    setLoadingTimeout(true);
    try {
      const payload = {
        data_retention_tenure: dataRetentionTenure || "12",
        backup_schedule: backupSchedule || "Monthly",
        local_storage: localStorageEnabled ? "1" : "0",
        local_storage_path: localStoragePath,
        cloud_storage: cloudStorageEnabled ? "1" : "0",
        cloud_storage_path: cloudStoragePath,
        is_min_eight_char: minEightChar ? "1" : "0",
        is_atleast_one_symbol: atLeastOneSymbol ? "1" : "0",
        is_atleast_one_number: atLeastOneNumber ? "1" : "0",
        is_include_lower_and_uppercase: includeLowerUppercase ? "1" : "0",
        inactivity_timeout: inactivityTimeout,
      };

      const response = await axiosInstance.post("/ems/save-data-management-security", payload);

      if (response.data.status === "success") {
        alert("Session timeout settings updated successfully!");
      } else {
        alert("Failed to save session timeout settings.");
      }
    } catch (error) {
      console.error("Error saving session timeout settings:", error.response?.data || error.message);
      alert("Error while saving session timeout settings: " + (error.response?.data?.message || error.message));
    } finally {
      setLoadingTimeout(false);
    }
  };

  if (loadingData) {
    return (
      <div className="tab-pane" id="data-management" role="tabpanel">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-pane" id="data-management" role="tabpanel">
      <div className="row">
        {/* Data Retention and Backup Section */}
        <div className="col-lg-6 border-end">
          <h6 className="mt-4">Data Retention</h6>
          <label>Data Retention Tenure:</label>
          <select
            className="form-select mb-3"
            value={dataRetentionTenure}
            onChange={(e) => setDataRetentionTenure(e.target.value)}
          >
            <option value="">Select Tenure</option>
            <option value="never">Never</option>
            <option value="6">6 Months</option>
            <option value="12">1 Year</option>
            <option value="24">2 Years</option>
          </select>
          <label>Backup Schedule:</label>
          <select
            className="form-select mb-3"
            value={backupSchedule}
            onChange={(e) => setBackupSchedule(e.target.value)}
          >
            <option value="">Select Schedule</option>
            <option value="Weekly">Weekly</option>
            <option value="Monthly">Monthly</option>
          </select>
          <h6 className="mt-4">Data Backup Destination</h6>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={localStorageEnabled}
              onChange={(e) => setLocalStorageEnabled(e.target.checked)}
            />
            <label>Local Storage</label>
          </div>
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Enter local storage path"
            value={localStoragePath}
            onChange={(e) => setLocalStoragePath(e.target.value)}
            disabled={!localStorageEnabled}
          />
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={cloudStorageEnabled}
              onChange={(e) => setCloudStorageEnabled(e.target.checked)}
            />
            <label>Cloud Storage</label>
          </div>
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Enter cloud storage path"
            value={cloudStoragePath}
            onChange={(e) => setCloudStoragePath(e.target.value)}
            disabled={!cloudStorageEnabled}
          />
          <button
            className="btn btn-outline-primary"
            onClick={handleSaveRetention}
            disabled={loadingRetention}
          >
            {loadingRetention ? (
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            ) : null}
            {loadingRetention ? "Saving..." : "Save"}
          </button>
        </div>

        {/* Security Settings Section */}
        <div className="col-lg-6">
          <h6 className="mt-4">Security Settings</h6>
          <h6>Password Policies</h6>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={minEightChar}
              onChange={(e) => setMinEightChar(e.target.checked)}
            />
            <label>Must contain at least 8 characters</label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={atLeastOneSymbol}
              onChange={(e) => setAtLeastOneSymbol(e.target.checked)}
            />
            <label>Must include at least one symbol (e.g., !@#$)</label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={atLeastOneNumber}
              onChange={(e) => setAtLeastOneNumber(e.target.checked)}
            />
            <label>Must include at least one number (0-9)</label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={includeLowerUppercase}
              onChange={(e) => setIncludeLowerUppercase(e.target.checked)}
            />
            <label>Must include both uppercase and lowercase</label>
          </div>
          <button
            className="btn btn-outline-primary mb-4"
            onClick={handleSavePasswordPolicies}
            disabled={loadingPassword}
          >
            {loadingPassword ? (
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            ) : null}
            {loadingPassword ? "Saving..." : "Save"}
          </button>

          <h6>Session Timeout Settings</h6>
          <h6>Inactivity Timeout:</h6>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="radio"
              name="timeout"
              value="2"
              checked={inactivityTimeout === "2"}
              onChange={(e) => setInactivityTimeout(e.target.value)}
            />
            <label>2 minutes</label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="radio"
              name="timeout"
              value="30"
              checked={inactivityTimeout === "30"}
              onChange={(e) => setInactivityTimeout(e.target.value)}
            />
            <label>30 minutes</label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="radio"
              name="timeout"
              value="60"
              checked={inactivityTimeout === "60"}
              onChange={(e) => setInactivityTimeout(e.target.value)}
            />
            <label>1 hour</label>
          </div>
          <button
            className="btn btn-outline-primary"
            onClick={handleSaveTimeout}
            disabled={loadingTimeout}
          >
            {loadingTimeout ? (
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            ) : null}
            {loadingTimeout ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Export the component as default
export default DataManagement;
